import EventEmitter from "events";
import { v4 as uuidV4 } from 'uuid';
import { DateTime } from "luxon";

import { DEFAULT_DATABASE_NAME, DEFAULT_REFETCH_AGE_DAYS, DEFAULT_SEED_STRATEGY_DAYS, DEFAULT_TILE_OBJECT_STORE_NAME, DEFAULT_UI_TITLE_OP_INPROGRESS, DEFAULT_OPERATION_OBJECT_STORE_NAME, DEFAULT_OPERATION_EVENT_CHANNEL_NAME, DEFAULT_OPERATION_HISTORY_MAX_AGE_DAYS } from "../../../ServiceWorker/TileCache/indexedDb/constants";
import { IIndexedDbTileCacheOptions } from "./indexedDB/types";
import { ITileCacheSeeder, ITileCacheSeedOperation, TileCacheSeedOperationChangeEvent, TileCacheSeedOperationMessageType, TileCacheSeedOperationStartedEvent, TileCacheSeedOperationStatus } from "./types";
import { Logic } from "./indexedDB/logic";
import { getTilesWithinBounds } from "../geoUtils";


/**
 * Class for a bulk seeder for use with IndexedDbTileCache
 */
export class BackgroundFetchTileCacheSeeder extends EventEmitter implements ITileCacheSeeder {

    constructor(
        private options: IIndexedDbTileCacheOptions = {}
    ) {
        super();

        this.options.databaseName = this.options.databaseName || DEFAULT_DATABASE_NAME;
        this.options.tileRefetchAgeDays = this.options.tileRefetchAgeDays || DEFAULT_REFETCH_AGE_DAYS;
        this.options.seedStrategy = this.options.seedStrategy || DEFAULT_SEED_STRATEGY_DAYS;
        this.options.operationObjectStoreName = this.options.operationObjectStoreName || DEFAULT_OPERATION_OBJECT_STORE_NAME;
        this.options.tileObjectStoreName = this.options.tileObjectStoreName || DEFAULT_TILE_OBJECT_STORE_NAME;
        this.options.eventBroadcastChannelName = this.options.eventBroadcastChannelName || DEFAULT_OPERATION_EVENT_CHANNEL_NAME;
        this.options.operationHistoryMaxAgeDays = this.options.operationHistoryMaxAgeDays || DEFAULT_OPERATION_HISTORY_MAX_AGE_DAYS;
        this.options.uiTitleOpInProgress = this.options.uiTitleOpInProgress ||  DEFAULT_UI_TITLE_OP_INPROGRESS;

        const broadcastChannel = new BroadcastChannel(this.options.eventBroadcastChannelName);
        broadcastChannel.onmessage = (event) => {
            const data = event.data as TileCacheSeedOperationChangeEvent;
            this.emitChangeEvent(data);
        };
    }

    public readonly id: string = "BackgroundFetchTileCacheSeeder";
    public readonly name: string = "Background Fetch";

    public isSupported = (): boolean => {
        return !!("BackgroundFetchManager" in window);
    }

    public maxInProgressDownloads = (): number => 3;

    public getSeedHistory = (): Promise<ITileCacheSeedOperation[]> => {
        return Logic.getOps(this.options);
    }

    public cullSeedHistory = (currentDate: DateTime): Promise<void> => {
        return Logic.cullOps(this.options, this.id, currentDate);
    }

    /**
     * Seeds an area of tiles by the given bounding box, the maximal z value and the optional minimal z value.
     *
     * Returns an operation handle with a unique identifier for the batch operation.
     */
    public seedFromBounds = async (crs: L.CRS, boundingBox: L.LatLngBounds, maxZoom: number, minZoom: number = 0, urlTemplate: string, urlSubdomains?: string[] | null, thumbUrl?: string | null): Promise<ITileCacheSeedOperation> => {

        if(!this.isSupported)
            throw new Error('BackgroundFetch is not supported on this browser');

        const numInProgress = await Logic.getNumInProgressOps(this.options);
        if (numInProgress >= this.maxInProgressDownloads())
            throw new Error("Maximum number of in-progress downloads reached");

        const tileCoords = getTilesWithinBounds(crs, boundingBox, maxZoom, minZoom);
        const tileCoordsToFetch = await Logic.filterCachedTileCoords(this.options, tileCoords, urlTemplate);
        const requestUrls = tileCoordsToFetch.map(c => { return { internalUrl: Logic.createInternalTileUrl(urlTemplate, c), requestUrl: Logic.createTileUrl(urlTemplate, urlSubdomains ?? [], c)} });

        const op: ITileCacheSeedOperation = {
            id: Logic.BGFETCH_ID_PREFIX + uuidV4(),
            seederId: this.id,
            status: TileCacheSeedOperationStatus.InProgress,
            startedAt: Date.now(),
            downloadedBytes: null,
            numTotal: tileCoordsToFetch.length,
            thumbUrl: thumbUrl ?? null,
            requestUrls
        };

        const swReg = await navigator.serviceWorker.ready;
        const bgFetchMgr = (swReg as any).backgroundFetch;

        await Logic.createOp(this.options, op);

        this.emitStartedEvent({ type: TileCacheSeedOperationMessageType.starting, op });

        if (op.numTotal === 0) {
            await Logic.updateOpStatus(this.options, op.id, TileCacheSeedOperationStatus.Succeeded, null, 0, null)
                .then((result) => {
                    if (result.didChange) {
                        this.emit(TileCacheSeedOperationMessageType.statusChange, { type: TileCacheSeedOperationMessageType.statusChange, opId: op.id, status: TileCacheSeedOperationStatus.Succeeded });
                        return Promise.resolve(op);
                    }
            });

            return Promise.resolve(op);
        }

        const bgFetch = await bgFetchMgr.fetch(
            op.id,
            requestUrls.map(ru => ru.requestUrl),
            {
                title: this.options.uiTitleOpInProgress
            },
        );

        bgFetch.addEventListener("progress", (e: any) => {
            const downloadedBytes: number = e.target.downloaded ?? 0;
            Logic.updateOpStatus(this.options, op.id, TileCacheSeedOperationStatus.InProgress, null, downloadedBytes)
                .then((result) => {
                    if (result.didChange)
                        this.emitChangeEvent({ type: TileCacheSeedOperationMessageType.progressChange, opId: op.id, downloadedBytes })
                });
        });

        this.emit(TileCacheSeedOperationMessageType.statusChange, { type: TileCacheSeedOperationMessageType.statusChange, opId: op.id, status: TileCacheSeedOperationStatus.InProgress });

        return op;
    }

    public canRemove = (op: ITileCacheSeedOperation): boolean => op.status === TileCacheSeedOperationStatus.Failed || op.status === TileCacheSeedOperationStatus.Cancelled || op.status === TileCacheSeedOperationStatus.Succeeded;
    public removeOperation = async (opId: string): Promise<ITileCacheSeedOperation> => {

        const op = await Logic.getOp(this.options, opId);
        if (!op)
            throw new Error("Operation not found");

        if (!this.canRemove(op))
            throw new Error("Operation is not in a suitable state");

        await Logic.deleteOp(this.options, opId);

        this.emitChangeEvent({ type: TileCacheSeedOperationMessageType.removed, opId: op.id });

        return op;
    }

    public canCancel = (op: ITileCacheSeedOperation): boolean => op.status === TileCacheSeedOperationStatus.InProgress;
    public cancelOperation = async (opId: string): Promise<ITileCacheSeedOperation> => {

        if(!this.isSupported)
            throw new Error('BackgroundFetch is not supported on this browser');

        const op = await Logic.getOp(this.options, opId);
        if (!op)
            throw new Error("Operation not found");

        if (!this.canCancel(op))
            throw new Error("Operation is not in a suitable state");

        const swReg = await navigator.serviceWorker.ready;
        const bgFetchMgr = (swReg as any).backgroundFetch;

        const fetchReg = await bgFetchMgr.get(opId);

        if (fetchReg) {
            await fetchReg.abort();
            await Logic.updateOpStatus(this.options, opId, TileCacheSeedOperationStatus.Cancelled, null, null);
            //An event will be fired from the background worker on abort - no need to do so here
        }
        else {
            //No fetch registration found, so - assume already aborted & fire change event
            await Logic.updateOpStatus(this.options, opId, TileCacheSeedOperationStatus.Cancelled, null, null)
                .then((result) => {
                    if (result.didChange) {
                        this.emitChangeEvent({ type: TileCacheSeedOperationMessageType.statusChange, opId, status: TileCacheSeedOperationStatus.Cancelled });
                    }
                });
            
        }

        

        return op;
    }


    //Internals
    private emitStartedEvent = (event: TileCacheSeedOperationStartedEvent) => {
        this.emit(event.type, event);
    }

    private emitChangeEvent = (event: TileCacheSeedOperationChangeEvent) => {
        this.emit(event.type, event);
    }

}